<template>
  <div class="user-management">
    <div class="page-header">
      <h1>User Management</h1>
    </div>

    <!-- Search -->
    <div class="search-bar">
      <a-input-search
        v-model:value="searchQuery"
        placeholder="Cari nama atau email..."
        style="width: 300px"
        @search="loadUsers"
        allow-clear
      />
    </div>

    <!-- Table -->
    <a-table
      :columns="columns"
      :data-source="users"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
      row-key="id"
    >
      <template #roleTag="{ record }">
        <a-tag :color="getRoleColor(record.role)">
          {{ getRoleLabel(record.role) }}
        </a-tag>
      </template>

      <template #action="{ record }">
        <div class="action-buttons">
          <a-button
            type="primary"
            danger
            shape="circle"
            size="large"
            @click="showEditModal(record)"
          >
            <edit-outlined />
          </a-button>
          <a-popconfirm
            title="Yakin ingin menghapus user ini?"
            ok-text="Ya"
            cancel-text="Tidak"
            @confirm="deleteUser(record)"
          >
            <a-button shape="circle" size="large" class="btn-delete">
              <delete-outlined />
            </a-button>
          </a-popconfirm>
        </div>
      </template>
    </a-table>

    <!-- Edit Modal -->
    <a-modal
      title="Edit User"
      :visible="modalVisible"
      :confirm-loading="modalLoading"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      width="600px"
    >
      <a-form :model="form" layout="vertical" ref="formRef">
        <a-form-item
          label="Nama"
          name="name"
          :rules="[{ required: true, message: 'Nama wajib diisi' }]"
        >
          <a-input v-model:value="form.name" placeholder="Masukkan nama" />
        </a-form-item>

        <a-form-item
          label="Email"
          name="email"
          :rules="[
            { required: true, message: 'Email wajib diisi' },
            { type: 'email', message: 'Email tidak valid' },
          ]"
        >
          <a-input v-model:value="form.email" placeholder="Masukkan email" />
        </a-form-item>

        <a-form-item label="Role">
          <div v-if="editingUser && editingUser.isLinked" class="linked-role-info">
            <a-icon type="lock" />
            <span>Role inherited from User table (read-only): {{ editingUser.role }}</span>
          </div>
          <a-form-item v-else name="role" no-style>
            <a-select v-model:value="form.role" placeholder="Pilih role">
              <a-select-option value="publik">Publik</a-select-option>
              <a-select-option value="admin">Admin</a-select-option>
              <a-select-option value="super_admin">Super Admin</a-select-option>
            </a-select>
          </a-form-item>
        </a-form-item>

        <a-form-item label="Pekerjaan" name="pekerjaan">
          <a-input v-model:value="form.pekerjaan" placeholder="Masukkan pekerjaan (opsional)" />
        </a-form-item>

        <a-form-item label="Institusi" name="institusi">
          <a-input v-model:value="form.institusi" placeholder="Masukkan institusi (opsional)" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons-vue'
import { mobileCmsUserAPI } from '@/services/axios/perki/mobileCms'

export default {
  name: 'UserManagement',
  components: { EditOutlined, DeleteOutlined },
  setup() {
    const loading = ref(false)
    const users = ref([])
    const searchQuery = ref('')
    const modalVisible = ref(false)
    const modalLoading = ref(false)
    const editingUser = ref(null)
    const formRef = ref(null)

    const form = reactive({
      name: '',
      email: '',
      role: '',
      pekerjaan: '',
      institusi: '',
    })

    const pagination = ref({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: total => `Total ${total} items`,
    })

    const sorter = reactive({ field: '', order: '' })

    const columns = computed(() => [
      { title: 'Nama', dataIndex: 'name', key: 'name', sorter: true, sortOrder: sorter.field === 'name' ? sorter.order : null },
      { title: 'Email', dataIndex: 'email', key: 'email' },
      { title: 'Role', key: 'roleTag', width: 120, slots: { customRender: 'roleTag' } },
      { title: 'Pekerjaan', dataIndex: 'pekerjaan', key: 'pekerjaan' },
      { title: 'Action', key: 'action', width: 120, slots: { customRender: 'action' } },
    ])

    const loadUsers = async () => {
      loading.value = true
      try {
        const params = {
          page: pagination.value.current,
          pageSize: pagination.value.pageSize,
          q: searchQuery.value,
        }
        if (sorter.field && sorter.order) {
          params.sortBy = sorter.field
          params.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc'
        }
        const response = await mobileCmsUserAPI.getAll(params)

        const result = response.data?.result || response.data?.data || {}
        const list =
          (Array.isArray(result.data) && result.data) ||
          (Array.isArray(result.users) && result.users) ||
          []

        users.value = list
        pagination.value.total = Number(result.total ?? list.length ?? 0)
      } catch (error) {
        message.error('Gagal memuat data user')
        console.error(error)
      } finally {
        loading.value = false
      }
    }

    const getRoleLabel = role => {
      if (!role || role === 'publik') return 'Publik'
      if (role === 'member') return 'Member'
      if (role === 'super_admin') return 'Super Admin'
      return role
    }

    const getRoleColor = role => {
      if (!role || role === 'publik') return 'default'
      if (role === 'member') return 'blue'
      return 'green'
    }

    const handleTableChange = (pag, _, tableSorter) => {
      pagination.value.current = pag.current
      pagination.value.pageSize = pag.pageSize
      if (tableSorter?.field) {
        sorter.field = tableSorter.field
        sorter.order = tableSorter.order || ''
      } else {
        sorter.field = ''
        sorter.order = ''
      }
      loadUsers()
    }

    const resetForm = () => {
      form.name = ''
      form.email = ''
      form.role = ''
      form.pekerjaan = ''
      form.institusi = ''
    }

    const showEditModal = record => {
      editingUser.value = record
      form.name = record.name
      form.email = record.email
      form.role = record.role
      form.pekerjaan = record.pekerjaan
      form.institusi = record.institusi
      modalVisible.value = true
    }

    const handleModalCancel = () => {
      modalVisible.value = false
      editingUser.value = null
      resetForm()
    }

    const handleModalOk = async () => {
      try {
        await formRef.value.validate()
      } catch {
        return
      }

      modalLoading.value = true
      try {
        const payload = {
          name: form.name,
          email: form.email,
          pekerjaan: form.pekerjaan,
          institusi: form.institusi,
        }

        // If user is linked, don't send role change
        if (editingUser.value && !editingUser.value.isLinked) {
          payload.role = form.role
        }

        await mobileCmsUserAPI.update(editingUser.value.id, payload)
        message.success('User berhasil diupdate')
        modalVisible.value = false
        editingUser.value = null
        resetForm()
        loadUsers()
      } catch (error) {
        const msg = error.response?.data?.result || 'Terjadi kesalahan'
        message.error(msg)
      } finally {
        modalLoading.value = false
      }
    }

    const deleteUser = async record => {
      try {
        await mobileCmsUserAPI.delete(record.id)
        message.success('User berhasil dihapus')
        loadUsers()
      } catch (error) {
        const msg = error.response?.data?.result || 'Gagal menghapus user'
        message.error(msg)
      }
    }

    onMounted(() => {
      loadUsers()
    })

    return {
      loading,
      users,
      searchQuery,
      pagination,
      modalVisible,
      modalLoading,
      editingUser,
      form,
      formRef,
      columns,
      getRoleLabel,
      getRoleColor,
      loadUsers,
      handleTableChange,
      showEditModal,
      handleModalCancel,
      handleModalOk,
      deleteUser,
    }
  },
}
</script>

<style scoped>
.user-management {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.search-bar {
  margin-bottom: 16px;
}

.linked-role-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 4px;
  color: #666;
}

.linked-role-info .anticon {
  color: #faad14;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-buttons .btn-delete {
  background-color: #333;
  border-color: #333;
  color: white;
}

.action-buttons .btn-delete:hover {
  background-color: #555;
  border-color: #555;
}
</style>
